import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from '@lingui/macro';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Pagination from '@decisiv/ui-components/lib/components/Pagination';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import { usePaginatedList } from 'hooks/usePaginatedList';

import { useAddAssetContext } from '../context/AddAssetContext';
import { collectAssetsToAdd } from '../utils/collectAssetsToAdd';
import { ITEMS_PER_PAGE } from '../StepTwo/constants';
import UnitNumberRow from './UnitNumberRow';
import { Container, List } from './styles';

export default function StepThree() {
  const { savedState, setSavedState, setStepThreeContinueButtonEnabled } =
    useAddAssetContext();

  const assets = useMemo(
    () => collectAssetsToAdd(savedState.stepTwo),
    [savedState.stepTwo],
  );

  const { unitNumbers } = savedState.addUnit;

  const ownedByCurrent = useMemo(
    () => savedState.stepTwo.ownedByCurrent ?? [],
    [savedState.stepTwo.ownedByCurrent],
  );

  // Existing unit numbers for VINs already in this depot, keyed by VIN. Used as
  // the displayed value until the user edits, so the current unit number always
  // shows even before the pre-fill below writes it into saved state.
  const existingUnitByVin = useMemo(
    () =>
      Object.fromEntries(
        ownedByCurrent
          .filter((asset) => asset.unitNumber)
          .map((asset) => [asset.vin, asset.unitNumber as string]),
      ),
    [ownedByCurrent],
  );

  // Pre-fill unit numbers for assets already in this depot so the edited value
  // is carried into registration. Runs once per set of current-owned assets and
  // only seeds VINs the user hasn't already touched.
  useEffect(() => {
    const seeded = ownedByCurrent.filter(
      (asset) => asset.unitNumber && !(asset.vin in unitNumbers),
    );
    if (seeded.length === 0) return;

    setSavedState((prev) => ({
      ...prev,
      addUnit: {
        ...prev.addUnit,
        unitNumbers: {
          ...Object.fromEntries(
            seeded.map((asset) => [asset.vin, asset.unitNumber as string]),
          ),
          ...prev.addUnit.unitNumbers,
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownedByCurrent, setSavedState]);

  const handleChange = useCallback(
    (vin: string, value: string) => {
      setSavedState((prev) => ({
        ...prev,
        addUnit: {
          ...prev.addUnit,
          unitNumbers: {
            ...prev.addUnit.unitNumbers,
            [vin]: value,
          },
        },
      }));
    },
    [setSavedState],
  );

  // Unit numbers are optional and accept any characters, so the user can always
  // proceed from this step.
  useEffect(() => {
    setStepThreeContinueButtonEnabled(true);
  }, [setStepThreeContinueButtonEnabled]);

  const { activePage, items, setCurrentPage, totalPages } = usePaginatedList({
    items: assets,
    perPage: ITEMS_PER_PAGE,
  });

  return (
    <Container>
      <Flex flexDirection="column">
        <H3>
          <Trans>Add Unit Number</Trans>
        </H3>
        <P size="small" shade={1}>
          <Trans>
            Unit numbers help identify assets within your fleet. You can add
            them now or update them later.
          </Trans>
        </P>
      </Flex>
      <List>
        {items.map(({ vin, year, make, model }) => (
          <UnitNumberRow
            key={vin}
            vin={vin}
            year={year}
            make={make}
            model={model}
            value={unitNumbers[vin] ?? existingUnitByVin[vin] ?? ''}
            onChange={handleChange}
          />
        ))}
      </List>
      {totalPages > 1 && (
        <Flex marginTop={1} alignSelf="flex-end">
          <Pagination
            totalPages={totalPages}
            activePage={activePage}
            onPageChange={setCurrentPage}
            defaultActivePage={1}
          />
        </Flex>
      )}
    </Container>
  );
}
